import { FC, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { format as formatTimeAgo } from "timeago.js";
import { Section } from "../../../components";
import { BACKEND_AI_AGENT_URL, supportedChainsMap } from "../../../config";
import { RequestAbortedError } from "../../../errors";
import {
  abbreviateNumber,
  tokenAmountDividedByDecimals,
} from "../../../utils/utils";
import { ArrowRightIcon } from "./ArrowRightIcon";
import { OpenInNewTabIcon } from "./OpenInNewTabIcon";

// Example: "en-US"
// undefined - use user's locale
const locale = undefined;

type SignificantTransfer = {
  fromAddress: string;
  toAddress: string;
  valueUSD: number;
  timestampMs: number;
  chainId: string;
  txHash: string;
  symbol: string;
  decimals: number;
  amount: string;
  tokenAddress: string;
};

const TransactionSide: FC<{
  amount: string;
  amountUSD: number;
  symbol: string;
  decimals: number;
  tokenAddress: string;
  chainId: string;
  className?: string;
}> = ({
  amount,
  amountUSD,
  symbol,
  decimals,
  tokenAddress,
  chainId,
  className,
}) => {
  return (
    <div className={`flex flex-col gap-1 ${className || ""}`}>
      <div className="flex items-center gap-1">
        <img
          src={`https://xswap.link/assets/tokens/${chainId}_${tokenAddress}_${symbol}.webp`}
          alt={`${symbol} logo`}
          className="w-[20px] h-[20px] min-w-[20px] min-h-[20px] max-w-[20px] max-h-[20px] rounded-full"
        />
        <div className="text-white font-medium text-xs whitespace-nowrap">
          {`${abbreviateNumber(tokenAmountDividedByDecimals({ amount, decimals }))} ${symbol}`}
        </div>
      </div>
      <div className="text-[10px] sm:text-xs font-medium text-[rgba(255,255,255,0.5)] whitespace-nowrap">
        {`${amountUSD.toLocaleString(undefined)} USD`}
      </div>
    </div>
  );
};

const AddressSide: FC<{ walletAddress: string; className?: string }> = ({
  walletAddress,
  className,
}) => {
  return (
    <div className={`flex items-center gap-1 ${className || ""}`}>
      <div className="hidden sm:block w-5 min-w-5 max-w-5 h-5 min-h-5 max-h-5 rounded-full bg-[#C4C4C4]"></div>
      <div className="text-white font-medium text-[10px] sm:text-xs">
        {walletAddress.slice(0, 4)}****
      </div>
    </div>
  );
};

export const SignificantTokenTransfers: FC = () => {
  const { projectId } = useParams();
  const [loading, setLoading] = useState(false);
  const [transfers, setTransfers] = useState<SignificantTransfer[]>([]);
  const fetchSignificantTransfersRef = useRef(new AbortController());

  const DATE_COLUMN_CLASS = "flex-1";
  const TRANSACTION_COLUMN_CLASS = "flex-1";
  const ADDRESS_COLUMN_CLASS = "flex-1";
  const OPEN_LINK_COLUMN_CLASS = "w-[60px] md:w-[80px]";

  useEffect(() => {
    (async () => {
      fetchSignificantTransfersRef.current.abort(new RequestAbortedError());
      fetchSignificantTransfersRef.current = new AbortController();
      setLoading(true);

      let response: Awaited<ReturnType<typeof fetch>>;
      try {
        response = await fetch(
          `${BACKEND_AI_AGENT_URL}/significant-transfers${projectId ? `/${projectId}` : ""}`,
          {
            signal: fetchSignificantTransfersRef.current.signal,
          },
        );
      } catch (err) {
        if (err instanceof RequestAbortedError) {
          return;
        }

        setLoading(false);
        throw err;
      }

      const data = (await response.json()) as {
        transfers: SignificantTransfer[];
      };

      setTransfers(data.transfers);
      setLoading(false);
    })();
  }, [projectId]);

  if (loading) {
    return (
      <>
        <Section size="xl">
          <h1 className="text-white font-bold text-base mb-5">Loading...</h1>
        </Section>
      </>
    );
  }

  if (!loading && !transfers.length) {
    return (
      <>
        <Section size="xl">
          <h1 className="text-white font-bold text-base mb-5">No transfers</h1>
        </Section>
      </>
    );
  }

  return (
    <>
      <Section size="xl">
        <h1 className="text-white font-bold text-base mb-5">
          Significant token transfers
        </h1>

        <div className="w-full relative flex flex-col gap-[10px] bg-[rgba(9,20,30,0.9)] rounded-2xl py-4 md:py-7">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="watermark watermark-bold"></div>
          </div>
          <div className="flex items-center text-sm font-normal text-[rgba(255,255,255,0.5)] gap-2 sm:gap-4 px-2 sm: px-4 md:px-7">
            <div className={`${DATE_COLUMN_CLASS}`}>Date</div>
            <div className={`${TRANSACTION_COLUMN_CLASS}`}>Transaction</div>
            <div className={`${ADDRESS_COLUMN_CLASS}`}>Address</div>
            <div
              className={`${OPEN_LINK_COLUMN_CLASS} text-right hidden sm:block`}
            >
              Open link
            </div>
          </div>
          <div className="flex flex-col">
            {transfers.map((transfer, index) => (
              <div
                key={`${transfer.txHash}`}
                className={`flex items-center py-2 gap-2 sm:gap-4 px-2 sm: px-4 md:px-7  ${index % 2 === 0 ? "" : "bg-[rgba(85,166,251,0.05)]"}`}
              >
                <div
                  className={`${DATE_COLUMN_CLASS} text-[10px] sm:text-xs md:text-base`}
                >
                  {formatTimeAgo(transfer.timestampMs, locale)}
                </div>
                <div
                  className={`${TRANSACTION_COLUMN_CLASS} flex items-center gap-1`}
                >
                  <TransactionSide
                    symbol={transfer.symbol}
                    decimals={transfer.decimals}
                    tokenAddress={transfer.tokenAddress}
                    amountUSD={transfer.valueUSD}
                    amount={transfer.amount}
                    chainId={transfer.chainId}
                  />
                  {/* @TODO when aggregators start scraping transactions with different tokenIn and tokenOut, we should uncomment this */}
                  {/* <ArrowRightIcon className="w-6 min-w-6 max-w-6 h-6 min-h-6 max-h-6" />
                <TransactionSide
                  symbol={transfer.symbol}
                  decimals={transfer.decimals}
                  tokenAddress={transfer.tokenAddress}
                  amountUSD={transfer.valueUSD}
                  amount={transfer.amount}
                  chainId={transfer.chainId}
                /> */}
                </div>
                <div
                  className={`${ADDRESS_COLUMN_CLASS} flex items-center gap-1`}
                >
                  <AddressSide walletAddress={transfer.fromAddress} />
                  <ArrowRightIcon className="w-6 min-w-6 max-w-6 h-6 min-h-6 max-h-6" />
                  <AddressSide walletAddress={transfer.toAddress} />
                </div>
                <div
                  className={`${OPEN_LINK_COLUMN_CLASS} text-center flex hidden sm:block`}
                >
                  <a
                    href={supportedChainsMap.evm[
                      transfer.chainId
                    ].getTransactionExplorerUrl(transfer.txHash)}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Go to blockchain scan"
                    className="ml-auto"
                  >
                    <OpenInNewTabIcon />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>
    </>
  );
};
